import {
  drawnFinishValue,
  FINISHES,
  LAYOUT_OPTION_VALUES,
  LAYOUTS,
  logoColorsOf,
  MOBILE_NAVS,
  offeredFinishValues,
  offeredLayoutOptions,
  PALETTES,
  paletteFromLogo,
  STUDIO_FINISH_GROUPS,
  SUGGESTED_ACCENTS,
  type FinishOptionKey,
  type FinishOptions,
  type PaletteSpec,
  darkModeOf,
  paletteSpec,
  type StudioChoice,
} from '@base/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from '../components/ui.tsx';
import { localized } from '../i18n/index.ts';
import { ColorAdjust } from './ColorParts.tsx';
import { ChoiceCard, LayoutThumb, OptionRow } from './pieces.tsx';
import {
  resolvedStudio,
  reviewOf,
  withAccent,
  withFinish,
  withFinishOption,
  withLayout,
  withLayoutOption,
  withPalette,
  optionKey,
} from './state.ts';

export interface StepProps {
  value: StudioChoice;
  onChange: (next: StudioChoice) => void;
  readOnly: boolean;
}

/** Etapa 1: o layout (sem cor), os ajustes dele e o menu no celular. */
export function LayoutStep({ value, onChange, readOnly }: StepProps) {
  const { t } = useTranslation();
  const options = resolvedStudio(value).layout;
  return (
    <>
      <Panel title={t('style.layoutLabel')}>
        <div
          role="radiogroup"
          aria-label={t('style.layoutLabel')}
          className="grid gap-3 @xs:grid-cols-2 @3xl:grid-cols-3"
        >
          {LAYOUTS.map((l) => (
            <ChoiceCard
              key={l.id}
              chosen={value.layout.id === l.id}
              disabled={readOnly}
              onClick={() => onChange(withLayout(value, l.id))}
              thumb={<LayoutThumb id={l.id} />}
              title={localized(l.name)}
              description={localized(l.description)}
            />
          ))}
        </div>
      </Panel>
      <Panel title={t('style.layoutAdjust')}>
        {offeredLayoutOptions(value.layout.id).map((key) => (
          <OptionRow
            key={key}
            label={t(`style.layoutOptions.${key}.label`)}
            values={LAYOUT_OPTION_VALUES[key]}
            value={options[key]}
            disabled={readOnly}
            labelOf={(v) => t(`style.layoutOptions.${key}.values.${v}`)}
            onChange={(v) => onChange(withLayoutOption(value, key, v))}
          />
        ))}
        <OptionRow
          label={t('style.mobileNav')}
          values={MOBILE_NAVS}
          value={value.mobileNav ?? 'drawer'}
          disabled={readOnly}
          labelOf={(v) => t(`style.mobileNavs.${v}`)}
          onChange={(v) => onChange({ ...value, mobileNav: v })}
        />
      </Panel>
    </>
  );
}

/** As cores de uma paleta lado a lado: fundo, lateral e destaque (cores de dado, não do tema). */
function PaletteSwatch({ p }: { p: PaletteSpec }) {
  const colors = [p.base?.paper ?? '#f5f6f8', p.sidebar?.bg ?? '#0b0e14', p.accent];
  return (
    <span aria-hidden className="flex h-8 overflow-hidden rounded-md border border-rule">
      {colors.map((c, i) => (
        <span key={i} className="flex-1" style={{ backgroundColor: c }} />
      ))}
    </span>
  );
}

/** As cores do logo, lidas num canvas (null enquanto lê; [] se não tem cor). */
function useLogoColors(logoUrl: string): string[] | null {
  const [colors, setColors] = useState<string[] | null>(null);
  useEffect(() => {
    let alive = true;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const size = 64;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx || !alive) return;
      ctx.drawImage(img, 0, 0, size, size);
      try {
        setColors(logoColorsOf(ctx.getImageData(0, 0, size, size).data));
      } catch {
        setColors([]);
      }
    };
    img.onerror = () => alive && setColors([]);
    img.src = logoUrl;
    return () => {
      alive = false;
    };
  }, [logoUrl]);
  return colors;
}

/** Etapa 2: a paleta (pronta ou do logo) e a cor de destaque. */
export function ColorStep({
  value,
  onChange,
  readOnly,
  logoUrl,
  brand,
}: StepProps & { logoUrl: string; brand?: ReactNode }) {
  const { t } = useTranslation();
  const logoColors = useLogoColors(logoUrl);
  const fromLogo = logoColors?.length ? paletteFromLogo(logoColors) : null;
  const accent = value.palette.accent;
  // Logo novo com a paleta do logo escolhida: a paleta acompanha as cores dele.
  const stored = value.palette.id === 'logo' ? value.palette.logoColors : undefined;
  useEffect(() => {
    if (stored && logoColors?.length && logoColors.join() !== stored.join())
      onChange({ ...value, palette: { ...value.palette, logoColors } });
  }, [logoColors, stored, value, onChange]);
  return (
    <>
      {brand}
      <Panel title={t('style.palettes')}>
        <div
          role="radiogroup"
          aria-label={t('style.palettes')}
          className="grid gap-3 @xs:grid-cols-2 @3xl:grid-cols-3"
        >
          {PALETTES.map((p) => (
            <ChoiceCard
              key={p.id}
              chosen={value.palette.id === p.id}
              disabled={readOnly}
              onClick={() => onChange(withPalette(value, p.id))}
              thumb={<PaletteSwatch p={p} />}
              title={localized(p.name)}
            />
          ))}
          <ChoiceCard
            chosen={value.palette.id === 'logo'}
            disabled={readOnly || !fromLogo}
            onClick={() => fromLogo && onChange(withPalette(value, 'logo', logoColors!))}
            thumb={fromLogo ? <PaletteSwatch p={fromLogo} /> : undefined}
            title={t('style.fromLogo')}
            description={
              logoColors === null
                ? t('style.fromLogoReading')
                : fromLogo
                  ? t('style.fromLogoHint')
                  : t('style.fromLogoNone')
            }
          />
        </div>
      </Panel>
      <Panel title={t('style.darkMode.label')}>
        {paletteSpec(value.palette.id)?.dark ? (
          <p className="text-sm text-muted">{t('style.darkMode.alreadyDark')}</p>
        ) : (
          <>
            <p className="text-sm text-muted">{t('style.darkMode.hint')}</p>
            <OptionRow
              label={t('style.darkMode.label')}
              values={['auto', 'off'] as const}
              value={darkModeOf(value)}
              disabled={readOnly}
              labelOf={(v) => t(`style.darkMode.values.${v}`)}
              onChange={(darkMode) => onChange({ ...value, darkMode })}
            />
          </>
        )}
      </Panel>
      <Panel title={t('style.accent')}>
        <p className="text-sm text-muted">{t('style.accentHint')}</p>
        <div
          className="flex flex-wrap items-center gap-2"
          role="radiogroup"
          aria-label={t('style.accent')}
        >
          {SUGGESTED_ACCENTS.map((color) => (
            <button
              key={color}
              type="button"
              role="radio"
              aria-checked={accent?.toLowerCase() === color}
              aria-label={t('style.accentPick', { color })}
              title={t('style.accentPick', { color })}
              disabled={readOnly}
              onClick={() => onChange(withAccent(value, color))}
              className={`flex size-9 items-center justify-center rounded-full border-2 ${
                accent?.toLowerCase() === color ? 'border-ink' : 'border-rule'
              }`}
            >
              <span className="size-6 rounded-full" style={{ backgroundColor: color }} />
            </button>
          ))}
          <input
            type="color"
            aria-label={t('style.accentOther')}
            disabled={readOnly}
            value={accent ?? reviewOf(value, '').theme.colors.light.accent ?? '#000000'}
            onChange={(e) => onChange(withAccent(value, e.target.value))}
            className="h-9 w-12 cursor-pointer rounded border border-control-border bg-sheet"
          />
          {accent && !readOnly && (
            <button
              type="button"
              onClick={() => onChange(withAccent(value, undefined))}
              className="text-sm text-brand-text underline-offset-2 hover:underline"
            >
              {t('style.accentReset')}
            </button>
          )}
        </div>
      </Panel>
      <ColorAdjust value={value} onChange={onChange} readOnly={readOnly} />
    </>
  );
}

/** Etapa 3: o acabamento e os ajustes dele que a base desenha. */
export function FinishStep({ value, onChange, readOnly }: StepProps) {
  const { t } = useTranslation();
  const finish = resolvedStudio(value).finish;
  const row = <K extends FinishOptionKey>(key: K) => (
    <OptionRow
      key={key}
      label={t(`style.finishOptions.${key}.label`)}
      values={offeredFinishValues(key)}
      value={drawnFinishValue(key, finish[key])}
      disabled={readOnly}
      labelOf={(v) => t(`style.finishOptions.${key}.values.${optionKey(v)}`)}
      onChange={(v: FinishOptions[K]) => onChange(withFinishOption(value, key, v))}
    />
  );
  return (
    <>
      <Panel title={t('style.finishes')}>
        <div
          role="radiogroup"
          aria-label={t('style.finishes')}
          className="grid gap-3 @xs:grid-cols-2 @3xl:grid-cols-3"
        >
          {FINISHES.map((f) => (
            <ChoiceCard
              key={f.id}
              chosen={value.finish.id === f.id}
              disabled={readOnly}
              onClick={() => onChange(withFinish(value, f.id))}
              title={localized(f.name)}
              description={localized(f.description)}
            />
          ))}
        </div>
      </Panel>
      <div className="flex flex-col gap-2">
        {STUDIO_FINISH_GROUPS.map((g, i) => (
          <details
            key={g.id}
            open={i === 0}
            className="ui-panel group rounded-lg bg-sheet [&_summary::-webkit-details-marker]:hidden"
          >
            <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 text-sm font-medium">
              {t(`style.finishGroups.${g.id}`)}
              <span className="text-xs font-normal text-muted">
                {t('style.adjustCount', { count: g.options.length })}
              </span>
            </summary>
            <div className="flex flex-col gap-4 border-t border-rule px-4 py-4">
              {g.options.map((k) => row(k))}
            </div>
          </details>
        ))}
      </div>
    </>
  );
}
